import { useEffect, useMemo, useState } from "react";
import Seo from "@/components/base/Seo";
import DataTable, { type DataColumn } from "@/components/base/DataTable";
import AdminPageHeader from "../components/AdminPageHeader";
import AdminStatCard from "../components/AdminStatCard";
import AdminToolbar from "../components/AdminToolbar";
import AdminFilterTabs from "../components/AdminFilterTabs";
import { useAuth } from "@/context/AuthContext";
import {
  getSecurityOverview,
  resolveSecurityAlert,
  runSecurityScan,
  type SecurityAlert,
  type SecuritySummary,
} from "@/services/securityApi";

const SEVERITY_TONE: Record<string, string> = {
  critical: "bg-secondary-100 text-secondary-900",
  high: "bg-primary-100 text-primary-900",
  medium: "bg-background-200 text-foreground-700",
  low: "bg-accent-100 text-accent-900",
};

const severityTabs = [
  { value: "all", label: "All" },
  { value: "critical", label: "Critical" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

const statusTabs = [
  { value: "open", label: "Open" },
  { value: "resolved", label: "Resolved" },
  { value: "all", label: "Everything" },
];

function SeverityBadge({ severity }: { severity: string }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold capitalize ${SEVERITY_TONE[severity] ?? SEVERITY_TONE.medium}`}>
      <i className={severity === "critical" || severity === "high" ? "ri-alarm-warning-fill" : "ri-error-warning-line"}></i>
      {severity}
    </span>
  );
}

export default function AdminSecurity() {
  const { token } = useAuth();
  const [alerts, setAlerts] = useState<SecurityAlert[]>([]);
  const [summary, setSummary] = useState<SecuritySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [severity, setSeverity] = useState("all");
  const [status, setStatus] = useState("open");
  const [scanning, setScanning] = useState(false);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    Promise.all([getSecurityOverview(token)])
      .then(([overview]) => {
        if (!active) return;
        setAlerts(overview.alerts);
        setSummary(overview.summary);
      })
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 3200);
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return alerts.filter((a) => {
      const matchesSeverity = severity === "all" || a.severity === severity;
      const matchesStatus = status === "all" || a.status === status;
      const matchesQuery =
        !q ||
        a.title.toLowerCase().includes(q) ||
        a.type.toLowerCase().includes(q) ||
        a.source.toLowerCase().includes(q) ||
        a.ip.toLowerCase().includes(q);
      return matchesSeverity && matchesStatus && matchesQuery;
    });
  }, [alerts, query, severity, status]);

  const openCount = alerts.filter((a) => a.status === "open").length;
  const criticalCount = alerts.filter((a) => a.status === "open" && a.severity === "critical").length;

  const handleScan = async () => {
    setScanning(true);
    try {
      const result = await runSecurityScan(token);
      const findings: SecurityAlert[] = result.findings.map((f) => ({
        id: f.id,
        severity: f.severity,
        type: f.type,
        title: f.title,
        detail: f.detail,
        source: f.subject.split(" · ")[0],
        subject: f.subject,
        ip: "—",
        status: "open",
        detectedAt: new Date().toLocaleString("en-IN", { hour12: false }),
        resolvedAt: null,
      }));
      setAlerts((prev) => [...findings, ...prev]);
      setSummary((prev) => (prev ? { ...prev, lastScanAt: new Date().toLocaleString("en-IN", { hour12: false }), openAlerts: prev.openAlerts + findings.length } : prev));
      flash(`Scan complete — ${result.scanned} entries checked, ${result.flagged} flagged.`);
    } catch {
      flash("Scan could not be completed. Please try again.");
    } finally {
      setScanning(false);
    }
  };

  const handleResolve = async (id: string) => {
    const previous = alerts.find((a) => a.id === id);
    setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, status: "resolved", resolvedAt: new Date().toISOString() } : a)));
    const ok = await resolveSecurityAlert(id, token);
    if (!ok) {
      setAlerts((prev) => prev.map((a) => (a.id === id ? previous ?? a : a)));
      flash("We couldn't resolve that alert. Please try again.");
      return;
    }
    setSummary((prev) => (prev ? { ...prev, openAlerts: Math.max(0, prev.openAlerts - 1), resolvedToday: prev.resolvedToday + 1 } : prev));
    flash("Alert marked as resolved.");
  };

  const columns: DataColumn<SecurityAlert>[] = [
    { key: "severity", header: "Severity", nowrap: true, render: (r) => <SeverityBadge severity={r.severity} /> },
    {
      key: "title",
      header: "Alert",
      render: (r) => (
        <div className="max-w-[360px]">
          <p className="font-semibold text-foreground-950">{r.title}</p>
          <p className="mt-0.5 text-xs text-foreground-500">{r.detail}</p>
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      nowrap: true,
      render: (r) => (
        <span className="inline-flex items-center gap-1 rounded-full bg-background-100 px-2.5 py-1 text-[11px] font-semibold text-foreground-700">
          <i className="ri-shield-flash-line"></i> {r.type}
        </span>
      ),
    },
    { key: "source", header: "Source", nowrap: true, render: (r) => <span className="font-mono text-xs text-foreground-600">{r.source}</span> },
    { key: "ip", header: "IP", nowrap: true, render: (r) => <span className="font-mono text-xs text-foreground-600">{r.ip}</span> },
    { key: "detectedAt", header: "Detected", nowrap: true, render: (r) => <span className="text-foreground-500">{r.detectedAt}</span> },
    {
      key: "status",
      header: "Status",
      nowrap: true,
      render: (r) =>
        r.status === "open" ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-bold text-secondary-900">
            <i className="ri-pulse-line"></i> Open
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-1 text-[11px] font-bold text-accent-900">
            <i className="ri-checkbox-circle-line"></i> Resolved
          </span>
        ),
    },
    {
      key: "actions",
      header: "Action",
      align: "right",
      nowrap: true,
      render: (r) =>
        r.status === "open" ? (
          <button
            type="button"
            onClick={() => handleResolve(r.id)}
            className="inline-flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-foreground-800 hover:border-accent-400 hover:text-accent-800 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-check-double-line"></i> Resolve
          </button>
        ) : (
          <span className="text-xs text-foreground-400">—</span>
        ),
    },
  ];

  return (
    <>
      <Seo title="Security" path="/admin/security" />

      <AdminPageHeader
        icon="ri-shield-check-line"
        title="Security dashboard"
        subtitle="Monitor threats, review alerts and run scans across new database entries."
        actions={
          <>
            {notice && (
              <span className="inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-3.5 py-2 text-sm text-accent-900">
                <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
              </span>
            )}
            <button
              type="button"
              onClick={handleScan}
              disabled={scanning}
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap cursor-pointer"
            >
              <i className={scanning ? "ri-loader-4-line animate-spin" : "ri-radar-line"}></i>
              {scanning ? "Scanning…" : "Run scan"}
            </button>
          </>
        }
      />

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <AdminStatCard label="Open alerts" value={String(summary?.openAlerts ?? openCount)} icon="ri-alert-line" tone="secondary" hint="Awaiting review" />
        <AdminStatCard label="Critical open" value={String(summary?.criticalOpen ?? criticalCount)} icon="ri-alarm-warning-line" tone="secondary" hint="Needs immediate action" />
        <AdminStatCard label="Resolved today" value={String(summary?.resolvedToday ?? 0)} icon="ri-checkbox-circle-line" tone="accent" hint="Closed in the last 24h" />
        <AdminStatCard
          label="File integrity"
          value={summary?.integrityOk ? "Verified" : "Changed"}
          icon={summary?.integrityOk ? "ri-file-shield-2-line" : "ri-file-warning-line"}
          tone={summary?.integrityOk ? "accent" : "secondary"}
          hint={`${summary?.watchedFiles ?? 0} files watched`}
        />
      </div>

      <div className="mt-4 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100/60 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${summary?.integrityOk ? "bg-accent-100 text-accent-800" : "bg-secondary-100 text-secondary-800"}`}>
            <i className={`${summary?.integrityOk ? "ri-file-shield-2-line" : "ri-file-warning-line"} text-lg`}></i>
          </span>
          <div>
            <p className="text-sm font-semibold text-foreground-950">
              Integrity baseline {summary?.integrityOk ? "matches" : "has drifted"}
            </p>
            <p className="text-xs text-foreground-500">
              Baseline created {summary?.integrityBaselineAt ?? "—"} · last verified {summary?.lastScanAt ?? "—"}
            </p>
          </div>
        </div>
        <span className="text-xs text-foreground-500">
          Deployed files are hashed on first boot; any unexpected change raises an alert.
        </span>
      </div>

      <div className="mt-6">
        <AdminToolbar query={query} onQuery={setQuery} placeholder="Search alerts by title, type, source or IP…">
          <div className="flex flex-wrap items-center gap-2">
            <AdminFilterTabs options={severityTabs} value={severity} onChange={setSeverity} />
            <AdminFilterTabs options={statusTabs} value={status} onChange={setStatus} />
          </div>
        </AdminToolbar>
      </div>

      <p className="mt-4 text-sm text-foreground-600">
        <span className="font-semibold text-foreground-950">{filtered.length}</span> of {alerts.length} alerts
      </p>

      <div className="mt-4">
        {loading ? (
          <div className="h-96 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
        ) : error ? (
          <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-6 text-center text-sm text-secondary-900">
            Couldn't load security alerts. Please refresh.
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.id}
            minWidth="1080px"
            empty={<p className="text-sm text-foreground-500">No alerts match these filters — nice and quiet.</p>}
          />
        )}
      </div>
    </>
  );
}